export function pad(n) {
  return String(n).padStart(2, "0");
}

// seconds -> "1h 05m" or "12m 30s"
export function fmtDur(sec) {
  sec = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${pad(m)}m`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}

// live clock timer mm:ss
export function fmtClock(sec) {
  sec = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

export function fmtMins(mins) {
  return fmtDur((mins || 0) * 60);
}

export const APP_TZ = "Pacific/Auckland";

export function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-NZ", { timeZone: APP_TZ, hour: "2-digit", minute: "2-digit", hour12: false });
}

// offset (minutes) of a timezone at a given instant
function tzOffsetMinutes(date, tz) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(date).map((x) => [x.type, x.value]));
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return (asUTC - date.getTime()) / 60000;
}

// UTC iso -> "YYYY-MM-DDTHH:mm" wall-clock in NZ (for datetime-local inputs)
export function isoToNZInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = tzOffsetMinutes(d, APP_TZ);
  return new Date(d.getTime() + off * 60000).toISOString().slice(0, 16);
}

// NZ wall-clock "YYYY-MM-DDTHH:mm" -> UTC iso
export function nzInputToISO(v) {
  if (!v) return null;
  const [dp, tp] = v.split("T");
  const [y, mo, da] = dp.split("-").map(Number);
  const [h, mi] = (tp || "00:00").split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  let utc = guess - tzOffsetMinutes(new Date(guess), APP_TZ) * 60000;
  utc = guess - tzOffsetMinutes(new Date(utc), APP_TZ) * 60000; // refine across DST edges
  return new Date(utc).toISOString();
}

// ISO date (YYYY-MM-DD) -> dd/mm/yyyy
export function fmtDMY(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

// 24h times in 15-minute blocks: ["", "00:00", "00:15", ... "23:45"]
export const TIME_OPTIONS = ["", ...Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const mm = (i % 4) * 15;
  return `${pad(h)}:${pad(mm)}`;
})];

export const WEEKDAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];


export function todayNZ() {
  // date-only string in NZ time, regardless of device timezone
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: APP_TZ, year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(new Date()).map((x) => [x.type, x.value])
  );
  return `${p.year}-${p.month}-${p.day}`;
}

export function ymd(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const STATUS_LABEL = {
  departing: "Departing",
  in_house: "In House",
  due: "Due",
};

export const STATUS_DESC = {
  departing: "Full clean",
  in_house: "Service clean",
  due: "Full clean — guest checking in",
};

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function recurrenceSummary(rec) {
  if (!rec) return "";
  switch (rec.type) {
    case "daily":
      return "Every day";
    case "every_n_days":
      return rec.interval > 1 ? `Every ${rec.interval} days` : "Every day";
    case "weekdays":
      return (rec.weekdays || []).length
        ? "Weekly · " + rec.weekdays.map((d) => WEEKDAYS[d]).join(", ")
        : "Weekly";
    case "day_of_month":
      return `Monthly · day ${rec.day_of_month}`;
    case "every_n_months":
      return `Every ${rec.interval} month${rec.interval > 1 ? "s" : ""} · day ${rec.day_of_month}`;
    case "once":
      return `One-off · ${rec.date || ""}`;
    default:
      return rec.type;
  }
}
